import React from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import LightTooltip from '@/components/utils/LightTooltip';
import { useTranslation } from 'react-i18next';
import { SubscriptionTier } from '@/../generated-client/models';
import COLORS from '@/resources/style/Themes';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { StringKeys } from '@/resources/strings/StringKeys';

const NO_SEATS_REMAINING = 0;
const TIER_SELECTOR_DEFAULT_MIN_WIDTH = 120;
const DEFAULT_SELECTOR_FONT_SIZE = 15;
const TRIAL_INFO_ICON_SIZE = 16;

interface Props {
  value: SubscriptionTier;
  onChange: (tier: SubscriptionTier) => void;
  disabled?: boolean;
  /** Tiers with no remaining seats are shown but non-selectable (block-and-explain lives in the hook). */
  fullPools?: SubscriptionTier[];
  sx?: SxProps<Theme>;
  fontSize?: number;
  /** Info badge shown while the organization trial elevates this user to Respond. */
  showTrialElevationInfo?: boolean;
  /** Which side of the dropdown the trial info badge sits on. */
  trialInfoSide?: 'left' | 'right';
  trialInfoIconSize?: number;
}

const TIER_ORDER: SubscriptionTier[] = [
  SubscriptionTier.Connect,
  SubscriptionTier.Ops,
  SubscriptionTier.Respond,
];

const TierSelector: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  fullPools = [],
  sx,
  fontSize = DEFAULT_SELECTOR_FONT_SIZE,
  showTrialElevationInfo = false,
  trialInfoSide = 'right',
  trialInfoIconSize = TRIAL_INFO_ICON_SIZE,
}) => {
  const { palette } = useTheme();
  const { t } = useTranslation();
  const trialElevationInfo = t(StringKeys.TRIAL_USER_RESPOND_ELEVATION_INFO);
  const layoutSx: SxProps<Theme> = {
    position: 'relative',
    width: '80%',
    minWidth: TIER_SELECTOR_DEFAULT_MIN_WIDTH,
  };
  const trialInfoIcon = (
    <LightTooltip title={trialElevationInfo}>
      <InfoIcon
        role="img"
        aria-hidden={false}
        aria-label={trialElevationInfo}
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: trialInfoIconSize,
          color: COLORS.BLUE_CRAYOLA,
          cursor: 'pointer',
          ...(trialInfoSide === 'left'
            ? { right: 'calc(100% + 4px)' }
            : { left: 'calc(100% + 4px)' }),
        }}
      />
    </LightTooltip>
  );

  return (
    <Box sx={sx ? [layoutSx, ...(Array.isArray(sx) ? sx : [sx])] : layoutSx}>
      <FormControl variant="outlined" fullWidth>
        <Select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value as SubscriptionTier)}
          sx={{
            backgroundColor: palette.grey[300],
            border: 'none',
            borderRadius: 2,
            height: 40,
            fontSize,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: `solid 2px ${COLORS.BLUE_CRAYOLA}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        >
        {TIER_ORDER.map((tier) => {
          const isFullPool =
            tier !== SubscriptionTier.Connect &&
            tier !== value &&
            fullPools.includes(tier);

          return (
            <MenuItem
              key={tier}
              value={tier}
              sx={{
                fontSize,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
              disabled={isFullPool}
            >
              <span>{getTierLabelKey(tier)}</span>
              {isFullPool && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: palette.error.main }}
                >
                  {NO_SEATS_REMAINING} {t(StringKeys.AVAILABLE)}
                </Typography>
              )}
            </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {showTrialElevationInfo && trialInfoIcon}
    </Box>
  );
};

export default TierSelector;
